import { apiFetch, getStateWithRevisionRequest, putStateWithRevisionRequest } from "./api";

export interface SiteServiceBlock {
  id: string;
  title: string;
  description: string;
  icon?: string;
  imageUrl?: string;
  active: boolean;
}

export interface SiteContentState {
  brandName: string;
  contactPhone: string;
  contactWhatsapp: string;
  contactEmail: string;
  address: string;
  mapsUrl: string;
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    description: string;
    primaryButtonText: string;
    primaryButtonLink: string;
    secondaryButtonText: string;
    imageUrl: string;
  };
  announcement: {
    enabled: boolean;
    text: string;
    buttonText: string;
    buttonLink: string;
    marquee: boolean;
  };
  services: SiteServiceBlock[];
  about: {
    title: string;
    description: string;
    imageUrl?: string;
  };
  seo: {
    homeTitle: string;
    homeDescription: string;
    servicesTitle: string;
    servicesDescription: string;
  };
}

export const DEFAULT_SITE_CONTENT: SiteContentState = {
  brandName: "Evren Çiftliği",
  contactPhone: "0535 723 05 28",
  contactWhatsapp: "905357230528",
  contactEmail: "info@evrenciftligi.com.tr",
  address: "Çatalca – İnceğiz Yolu\nİstanbul, Türkiye",
  mapsUrl: "https://maps.app.goo.gl/akpR7CaqthwVajmX7",
  hero: {
    eyebrow: "Çatalca · İnceğiz Yolu",
    title: "Evren Çiftliği",
    subtitle: "Kurbanlık • Adaklık",
    description: "Uzman ekip, hijyenik kesim ve zamanında teslimat ile sevdiklerinize güvenle hizmet sunuyoruz.",
    primaryButtonText: "Müşteri Girişi",
    primaryButtonLink: "/giris",
    secondaryButtonText: "WhatsApp ile Kayıt Ol",
    imageUrl: "https://storage.readdy-site.link/project_files/617e2d57-bc82-454a-909d-4c3baccc9515/5210b6d6-20ed-4d9a-a316-2414274aa6c0_ChatGPT-Image-31-Mar-2026-00_04_35.png?v=5d7c553c8368b2c97240597ae7d07f85",
  },
  announcement: {
    enabled: false,
    text: "2026 Kurban Bayramı rezervasyonlarımız başlamıştır.",
    buttonText: "Bilgi Al",
    buttonLink: "#iletisim",
    marquee: false,
  },
  services: [
    { id: "kurbanlik", title: "Kurbanlık Satışı", description: "Büyükbaş ve küçükbaş kurbanlık seçenekleri.", icon: "ri-seedling-line", active: true },
    { id: "adak", title: "Adak Hizmeti", description: "Adak kesimi ve isteğe bağlı dağıtım organizasyonu.", icon: "ri-heart-3-line", active: true },
    { id: "kesim", title: "Kesim & Parçalama", description: "Kesim, parçalama, paketleme ve teslimat süreci.", icon: "ri-scissors-2-line", active: true },
    { id: "tasima", title: "Taşıma Hizmeti", description: "Teslimat ve taşıma operasyonu takibi.", icon: "ri-truck-line", active: true },
  ],
  about: {
    title: "Güvenilir ve şeffaf çiftlik hizmeti",
    description: "Evren Çiftliği olarak kurbanlık, adaklık, kesim, parçalama ve teslimat süreçlerini takip edilebilir ve güvenilir bir şekilde yürütüyoruz.",
    imageUrl: "",
  },
  seo: {
    homeTitle: "Evren Çiftliği",
    homeDescription: "Kurbanlık, adaklık, kesim, parçalama ve teslimat hizmetleri.",
    servicesTitle: "Hizmetlerimiz",
    servicesDescription: "Evren Çiftliği hizmetleri.",
  },
};

let current: SiteContentState = DEFAULT_SITE_CONTENT;
let revision = 1;
const listeners = new Set<() => void>();

function mergeContent(value?: Partial<SiteContentState> | null): SiteContentState {
  const raw = value ?? {};
  return {
    ...DEFAULT_SITE_CONTENT,
    ...raw,
    hero: { ...DEFAULT_SITE_CONTENT.hero, ...(raw.hero ?? {}) },
    announcement: { ...DEFAULT_SITE_CONTENT.announcement, ...(raw.announcement ?? {}) },
    services: Array.isArray(raw.services) && raw.services.length > 0 ? raw.services : DEFAULT_SITE_CONTENT.services,
    about: { ...DEFAULT_SITE_CONTENT.about, ...(raw.about ?? {}) },
    seo: { ...DEFAULT_SITE_CONTENT.seo, ...(raw.seo ?? {}) },
  };
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function getSiteContent() {
  return current;
}

export function whatsappUrl(content = current) {
  const normalized = String(content.contactWhatsapp || content.contactPhone).replace(/\D/g, "");
  const withCountry = normalized.startsWith("90") ? normalized : `90${normalized.replace(/^0/, "")}`;
  return `https://wa.me/${withCountry}`;
}

export function telHref(content = current) {
  const normalized = String(content.contactPhone).replace(/\D/g, "");
  const withCountry = normalized.startsWith("90") ? normalized : `90${normalized.replace(/^0/, "")}`;
  return `+${withCountry}`;
}

export async function rehydrateSiteContentPublic() {
  try {
    current = mergeContent(await apiFetch<SiteContentState>("/state/public/site_content"));
    emit();
  } catch {
    current = DEFAULT_SITE_CONTENT;
    emit();
  }
  return current;
}

export async function rehydrateSiteContentAdmin() {
  try {
    const result = await getStateWithRevisionRequest<SiteContentState>("site_content");
    current = mergeContent(result.data);
    revision = result.revision;
    emit();
  } catch {
    current = DEFAULT_SITE_CONTENT;
    emit();
  }
  return current;
}

export async function saveSiteContent(next: SiteContentState) {
  const result = await putStateWithRevisionRequest<SiteContentState>("site_content", mergeContent(next), revision);
  current = mergeContent(result.data);
  revision = result.revision;
  emit();
  return current;
}

export function subscribeSiteContent(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
